import isNil from 'lodash/isNil'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { formatMonetaryAmount } from '@omnea-app/packages/universal/currency-utils/formatCurrency'
import { getDefaultFxRate } from '@omnea-app/packages/universal/price/getDefaultFxRate'
import { getPriceFrequencyLabel } from '@omnea-app/packages/universal/price/getPriceFrequencyLabel'
import { Price } from '@omnea-app/packages/universal/price/price'
import { getAmountInDefaultCurrency } from '@omnea-app/requests/price/getPriceInDefaultCurrency'
import { useTranslationsContext } from './TranslationsContext'
import { useFormatter } from './useFormatter'

type PriceFormatterOptions = { hideFrequency?: boolean }

export function usePriceFormatter({ hideFrequency = false }: PriceFormatterOptions = {}) {
  const { priceData } = useTranslationsContext()
  const { currenciesByCode, defaultCurrency } = priceData
  const { t } = useTranslation('requests')
  const { locale } = useFormatter()

  const formatPrice = useCallback(
    (price: Price) => {
      const frequencyLabel =
        price.paymentInterval != null && !hideFrequency
          ? getPriceFrequencyLabel({ paymentInterval: price.paymentInterval, multiYears: price.multiYears, t })
          : undefined
      const formattedPrice = `${formatMonetaryAmount(price.amount, price.currency, locale)}${!isNil(frequencyLabel) ? ` ${frequencyLabel}` : ''}`

      const defaultFxRate = getDefaultFxRate({ currenciesByCode, defaultCurrency })

      const formattedDefaultPrice = !isNil(price.fxRate)
        ? `~${formatMonetaryAmount(price.amount / (defaultFxRate * price.fxRate), defaultCurrency, locale)}`
        : undefined

      return {
        formattedPrice,
        formattedDefaultPrice,
      }
    },
    [hideFrequency, currenciesByCode, defaultCurrency, locale, t]
  )

  const getAmountInDefaultCurrencyFn = useCallback(
    (price: Price): number | undefined => {
      return getAmountInDefaultCurrency({ price, defaultCurrency, currenciesByCode })
    },
    [defaultCurrency, currenciesByCode]
  )

  return {
    formatPrice,
    currenciesByCode,
    defaultCurrency,
    getAmountInDefaultCurrency: getAmountInDefaultCurrencyFn,
  }
}
